import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';

interface Article {
  id: string;
  title: string;
  summary: string;
  content: string;
  date: string;
  columnType: 'industry' | 'country';
  category: string;
  link: string;
  image: string;
  status: 'draft' | 'published';
}

const Home: React.FC = () => {
  const [selectedLanguage, setSelectedLanguage] = useState('简体中文');
  const [isLanguageDropdownOpen, setIsLanguageDropdownOpen] = useState(false);
  const [latestArticles, setLatestArticles] = useState<Article[]>([]);
  const [isLoadingArticles, setIsLoadingArticles] = useState(true);
  const navigate = useNavigate();

  // handle article click if needed
  const handleArticleClick = (articleId: string) => {
    navigate(`/zh/news/detail/${articleId}`);
  };

  // 加载最新文章
  useEffect(() => {
    loadLatestArticles();
  }, []);

  const loadLatestArticles = async () => {
    setIsLoadingArticles(true);
    try {
      const { data, error } = await supabase
        .from('articles')
        .select('*')
        .eq('status', 'published')
        .order('date', { ascending: false })
        .limit(3);

      if (error) throw error;
      
      if (data && data.length > 0) {
        setLatestArticles(data as Article[]);
      } else {
        // 如果数据库中没有文章，使用默认示例数据
        setLatestArticles(defaultArticles);
      }
    } catch (error) {
      console.error('加载文章失败:', error);
      // 加载失败时使用默认示例数据
      setLatestArticles(defaultArticles);
    } finally {
      setIsLoadingArticles(false);
    }
  };

  const defaultArticles: Article[] = [
    {
      id: '1',
      title: '印尼数字经济发展现状与前景分析',
      summary: '深入分析印度尼西亚数字经济的发展现状，探讨其在东南亚地区的重要地位以及未来发展机遇与挑战。',
      date: '2024-01-15',
      columnType: 'country',
      category: '印度尼西亚',
      link: '',
      image: 'https://public.readdy.ai/ai/img_res/24c29fd39e5074ecfe3c4388da0a77c9.jpg',
      status: 'published',
      content: ''
    },
    {
      id: '2',
      title: '东南亚金融科技创新趋势报告',
      summary: '全面解读东南亚地区金融科技的最新发展趋势，分析数字支付、区块链等技术在该地区的应用前景。',
      date: '2024-01-12',
      columnType: 'industry',
      category: '金融科技',
      link: '',
      image: 'https://readdy.ai/api/search-image?query=Southeast%20Asia%20fintech%20innovation%20digital%20payment%20blockchain%20technology%20mobile%20financial%20services%20modern%20business&width=800&height=400&seq=home2&orientation=landscape',
      status: 'published',
      content: ''
    },
    {
      id: '3',
      title: '越南制造业转型升级路径研究',
      summary: '探讨越南制造业在全球供应链重构背景下的转型升级策略，分析其在智能制造领域的发展机遇。',
      date: '2024-01-10',
      columnType: 'country',
      category: '越南',
      link: '',
      image: 'https://readdy.ai/api/search-image?query=Vietnam%20manufacturing%20industry%20transformation%20smart%20factory%20automation%20technology%20industrial%20development%20modern%20production&width=800&height=400&seq=home3&orientation=landscape',
      status: 'published',
      content: ''
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="sticky top-0 z-50 flex items-center justify-center gap-2 md:h-20 bg-[#071C74] px-4 py-4 font-medium md:flex-none md:justify-start md:p-2 md:px-3">
        <div className="sticky top-0 flex h-7 items-center justify-between md:h-20 w-full max-w-7xl mx-auto">
          <div className="adi-header-left">
            <a href="/">
              <img 
                alt="ADI Logo" 
                width={125} 
                height={56} 
                src="https://static.readdy.ai/image/4d7c1148f372075ad366b96364633fcd/3900b388860a5efa532a02080a385ec7.png" 
                className="h-14 w-auto"
              />
            </a>
          </div>
          
          <div className="hidden md:flex items-center space-x-4 text-white">
            <div className="relative">
              <div className="flex bg-white/10 backdrop-blur-sm rounded-full p-1">
                <button 
                  className="px-4 py-2 text-white bg-white/20 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap"
                  onClick={() => {
                    setSelectedLanguage('简体中文');
                    setIsLanguageDropdownOpen(false);
                  }}
                >
                  简体中文
                </button>
                <a 
                  href="/en"
                  className="px-4 py-2 text-white/70 hover:text-white hover:bg-white/10 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap"
                >
                  English
                </a>
              </div>
            </div>
            <a 
              href="/flanexus"
              className="px-4 py-2 text-white hover:text-blue-200 transition-colors whitespace-nowrap"
            >
              炎禾智联
            </a>
            
            <a 
              href="/zh/news"
              className="px-4 py-2 text-white hover:text-blue-200 transition-colors"
            >
              资讯
            </a>
            
            <a 
              href="/zh/contact-us"
              className="px-4 py-2 border border-white rounded-full hover:bg-white hover:text-[#071C74] transition-colors text-white"
            >
              联系我们
            </a>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section 
        className="h-[920px] md:h-[880px] bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: `url("https://static.readdy.ai/image/4d7c1148f372075ad366b96364633fcd/3138850d221a5ab660120f205947f752.jpeg")`
        }}
      >
        <div className="max-w-7xl mx-auto h-full w-full px-4 relative z-10">
          <div className="flex w-full items-center h-full">
            <div className="flex max-w-[100%] flex-col justify-start text-left lg:max-w-[550px]">
              <h1 className="font-bold text-white text-[40px] leading-[60px] md:text-[60px] md:leading-[80px] lg:text-[80px] xl:text-[90px] xl:leading-[110px] mb-6">
                亚数智库
              </h1>
              <p className="text-left text-sm text-white/85 md:text-[15px] mb-12 leading-relaxed">
                ADI 致力于在亚太地区构建一个专业的数字化市场服务平台,为企业提供从规划到实施等全方位的数字化服务。我们以深入的咨询服务为基础,结合丰富的实践经验、行业专家见解以及先进的方法论工具,为企业提供量身定制的解决方案和实施服务。ADI 致力于提升企业项目的成功率,优化投资回报,并有效管理风险。
              </p>
              <a 
                href="/zh/contact-us"
                className="inline-block bg-blue-600 text-white px-8 py-4 rounded-lg hover:bg-blue-700 transition-colors font-medium text-lg w-fit whitespace-nowrap"
              >
                联系我们
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Accelerate Thinking Section */}
      <section className="bg-[#101214] px-4 pb-[80px] md:px-0 md:pb-[180px]">
        <div className="max-w-7xl mx-auto">
          <div className="pt-[100px] text-center md:mb-[80px]">
            <h2 className="text-[36px] font-bold leading-[54px] text-white md:text-[40px] md:leading-[60px] mb-4">
              加速思考
            </h2>
            <p className="mx-auto max-w-[540px] text-[15px] leading-[22px] text-white/85">
              帮助CXO人群评估全渠道和数字化解决方案等基本战略问题
            </p>
          </div>
          
          <div className="flex flex-col justify-center text-white md:flex-row gap-[5%]">
            <div className="mt-[64px] flex flex-col items-center justify-start px-6 text-center md:mt-0 md:w-[30%] md:p-0">
              <img 
                alt="活动" 
                width={88} 
                height={88} 
                src="https://static.readdy.ai/image/4d7c1148f372075ad366b96364633fcd/ba1fc11c2682fe53fc8766c439065671.webp" 
                className="mb-6"
              />
              <h3 className="mb-[12px] text-[22px] leading-[32px] text-white md:text-[25px] md:leading-[38px] font-bold">
                活动
              </h3>
              <p className="text-sm text-white/85 leading-relaxed">
                沙龙和论坛作为CIO/CDO的专属智库,旨在推动东南亚地区数字化转型趋势和挑战的战略对话,以促进创新和可持续增长。
              </p>
            </div>
            
            <div className="mt-[64px] flex flex-col items-center justify-start px-6 text-center md:mt-0 md:w-[30%] md:p-0">
              <img 
                alt="洞见" 
                width={88} 
                height={88} 
                src="https://static.readdy.ai/image/4d7c1148f372075ad366b96364633fcd/9aa15216153dec80ea9994cab46aa0bd.webp" 
                className="mb-6"
              />
              <h3 className="mb-[12px] text-[22px] leading-[32px] text-white md:text-[25px] md:leading-[38px] font-bold">
                洞见
              </h3>
              <p className="text-sm text-white/85 leading-relaxed">
                研究亚太地区的数字化进程,包括技术进步、政策变化、市场趋势、消费者行为以及数字化对经济和社会的影响。
              </p>
            </div>
            
            <div className="mt-[64px] flex flex-col items-center justify-start px-6 text-center md:mt-0 md:w-[30%] md:p-0">
              <img 
                alt="执行" 
                width={88} 
                height={88} 
                src="https://static.readdy.ai/image/4d7c1148f372075ad366b96364633fcd/5cbd8cfcfb9c492ebf61eb4618de24f5.webp" 
                className="mb-6"
              />
              <h3 className="mb-[12px] text-[22px] leading-[32px] text-white md:text-[25px] md:leading-[38px] font-bold">
                执行
              </h3>
              <p className="text-sm text-white/85 leading-relaxed">
                通过数字化咨询与实施,帮助组织评估其当前的数字化水平、规划未来的数字化战略,并执行这些战略以实现业务目标的过程。
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Cooperation and Support Section */}
      <section className="mb-[80px] px-5 md:mb-[140px]">
        <div className="max-w-7xl mx-auto">
          <div className="mb-[50px] pt-[80px] text-center md:mb-[80px] md:pt-[120px]">
            <h2 className="text-[36px] font-bold leading-[54px] text-black md:text-[40px] md:leading-[60px] mb-4">
              合作与支持
            </h2>
            <p className="text-[14px] leading-[24px] text-black/65">
              通过ADI顾问网络,企业将获得国际视野和最佳业务实践
            </p>
          </div>
          
          <div className="flex flex-wrap justify-between gap-4">
            {[
              {
                icon: "ri-bar-chart-line",
                title: "行业洞察",
                description: "通过深入研究和分析行业趋势、市场动态和技术进步,ADI帮助客户获得对行业的深刻理解"
              },
              {
                icon: "ri-pie-chart-line",
                title: "市场研究",
                description: "通过持续调研与和分析,ADI积累了丰富的市场知识,能够为客户提供专业的市场见解和策略"
              },
              {
                icon: "ri-team-line",
                title: "战略联盟",
                description: "ADI与行业领袖和决策者建立合作关系,帮助客户扩大影响力并获取关键资源"
              },
              {
                icon: "ri-chat-3-line",
                title: "对话讨论",
                description: "ADI通过与客户紧密沟通,了解他们的业务目标和挑战,从而提供定制化的解决方案"
              },
              {
                icon: "ri-arrow-up-line",
                title: "持续互利",
                description: "ADI致力于与客户建立长期合作关系,通过提供长期价值,确保双方都能从合作中获益"
              },
              {
                icon: "ri-settings-3-line",
                title: "技术合作",
                description: "由Flanexus提供技术支持,研究院通过与Flanexus合作,利用其技术能力来增强自己的服务"
              },
              {
                icon: "ri-tools-line",
                title: "实施资源",
                description: "ADI研究院不仅提供战略咨询,还整合实施资源,能够确保咨询建议被有效执行"
              },
              {
                icon: "ri-trophy-line",
                title: "影响力",
                description: "ADI研究院能够提供高质量的服务,并在客户的战略决策中发挥重要作用"
              }
            ].map((item, index) => (
              <div 
                key={index}
                className="group w-[100%] md:w-[23.5%] mt-[20px] rounded-xl border border-[#A2A2A2] p-4 hover:border-[#2D66F4] hover:bg-[#2D66F4] hover:shadow-[0_4px_12px_4px_rgba(45,102,244,0.2)] transition-all duration-300"
              >
                <div className="w-16 h-16 flex items-center justify-center bg-[#1E40AF] group-hover:bg-white rounded-full mb-4">
                  <i className={`${item.icon} text-2xl text-white group-hover:text-[#1E40AF] transition-colors duration-300`}></i>
                </div>
                <h4 className="mb-[8px] mt-[18px] text-[22px] font-bold leading-[30px] text-black group-hover:text-white transition-colors duration-300">
                  {item.title}
                </h4>
                <p className="text-[14px] leading-[20px] text-black/75 group-hover:text-white/75 transition-colors duration-300">
                  {item.description}
                </p>
              </div>
            ))}
          </div>

        </div>
      </section>

      {/* Mission and Vision Section */}
      <section className="bg-[#f8f8f8] px-5 pb-[80px] pt-[50px] md:bg-white md:px-0 md:pb-0 md:pt-0">
        <div className="max-w-7xl mx-auto">
          <div className="mb-[80px] pt-[20px] text-center">
            <h2 className="text-[32px] font-bold leading-[48px] text-black md:text-[40px] md:leading-[60px] mb-4">
              使命和愿景
            </h2>
            <p className="text-[14px] leading-[24px] text-black/65">
              塑造亚太数字新未来,注入全球化发展新动力
            </p>
          </div>
          
          <div 
            className="relative rounded-lg bg-contain bg-cover bg-center bg-no-repeat min-h-[400px] md:min-h-[500px]"
            style={{
              backgroundImage: "url('https://static.readdy.ai/image/4d7c1148f372075ad366b96364633fcd/af0932ef1096903a71e0bac36d4f092b.png')"
            }}
          >
            <div className="p-10 pb-[70px] pt-[60px] md:pb-[110px] md:pl-[100px] md:pt-[100px]">
              <div className="flex flex-wrap pb-1 pt-4 md:pt-8 lg:flex-nowrap mb-8">
                <div className="mr-6 text-nowrap text-left text-[26px] font-bold leading-[42px] text-[#2D66F4] md:text-right md:text-[36px] min-w-[10%]">
                  使命
                </div>
                <div className="text-[14px] font-normal leading-[20px] text-white md:text-[17px] md:leading-[26px] max-w-[72%] md:text-[20px] md:leading-[32px]">
                  作为亚洲地区,尤其是东南亚的数字化转型领航者,ADI 致力于推动可持续和包容性的数字创新。我们通过深度行业洞察、前沿技术研究和最佳实践分享,为企业提供全方位的数字化转型指导,助力构建更加智能、高效、可持续的数字生态系统。
                </div>
              </div>
              
              <div className="flex flex-wrap pb-1 pt-4 md:pt-8 lg:flex-nowrap">
                <div className="mr-6 text-nowrap text-left text-[26px] font-bold leading-[42px] text-[#2D66F4] md:text-right md:text-[36px] min-w-[10%]">
                  愿景
                </div>
                <div className="text-[14px] font-normal leading-[20px] text-white md:text-[17px] md:leading-[26px] max-w-[72%] md:text-[20px] md:leading-[32px]">
                  构建由顶尖首席信息官和数字领袖组成的生态系统,通过 ADI 行业领先的咨询和尖端技术解决方案,为企业提供战略性指导。我们致力于成为亚太地区最具影响力的数字化转型智库,推动区域数字经济的蓬勃发展,为全球数字化进程贡献亚洲智慧。
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Digital Framework Section */}
      <section className="px-4 py-20 pt-12 md:px-0 md:py-[140px]">
        <div className="max-w-7xl mx-auto">
          <div className="mb-[80px] pt-[20px] text-center">
            <h2 className="text-[32px] font-bold leading-[48px] text-black md:text-[40px] md:leading-[60px] mb-16 md:mb-20">
              ADI 数字化转型框架
            </h2>
            
            <div className="flex flex-col-reverse md:flex-row">
              <div className="md:mr-[6%] md:min-w-80">
                <img 
                  alt="ADI Digital Framework" 
                  width={320} 
                  height={428} 
                  src="https://static.readdy.ai/image/4d7c1148f372075ad366b96364633fcd/d84b8d67d78853085d6b29988d0ab755.webp" 
                  className="w-full flex-none"
                />
              </div>
              
              <div className="flex flex-wrap gap-3 md:gap-7 md:min-w-[calc(100%-320px)]">
                {[
                  {
                    icon: "https://static.readdy.ai/image/4d7c1148f372075ad366b96364633fcd/f88173fafa25b98d56951a8503453d55.webp",
                    title: "战略与合规",
                    description: "帮助企业实现战略目标,同时确保符合本地法规,强化数据安全与治理,为可持续发展奠定基础。"
                  },
                  {
                    icon: "https://static.readdy.ai/image/4d7c1148f372075ad366b96364633fcd/1011d9e1fa4f5584b7bdc67deb384100.webp",
                    title: "客户与市场洞察",
                    description: "以客户为中心,支持多样化支付方式、多语言服务和移动优化,助力企业快速适应市场变化。"
                  },
                  {
                    icon: "https://static.readdy.ai/image/4d7c1148f372075ad366b96364633fcd/4eddb1d537c464fde499766199e9a00f.webp",
                    title: "技术能力与拓展性",
                    description: "提供灵活、可扩展的技术解决方案,支持云端部署、灾备规划与技术升级,确保业务高效稳定运行。"
                  },
                  {
                    icon: "https://static.readdy.ai/image/4d7c1148f372075ad366b96364633fcd/6e2dd49e7085f5315eaae51334510156.webp",
                    title: "财务与运营效率",
                    description: "优化核心业务功能,降低成本风险,同时提供培训支持,助力企业实现长期价值。"
                  },
                  {
                    icon: "https://static.readdy.ai/image/4d7c1148f372075ad366b96364633fcd/5cbd8cfcfb9c492ebf61eb4618de24f5.webp",
                    title: "创新与生态合作",
                    description: "通过数据分析挖掘潜力,构建开放的合作生态,与企业共同推动数字化创新。"
                  }
                ].map((item, index) => (
                  <div key={index} className="flex w-[47%] flex-col md:w-[29.5%]">
                    <img 
                      alt={item.title} 
                      width={64} 
                      height={64} 
                      src={item.icon}
                      className="mb-4"
                    />
                    <h4 className="text-left font-bold leading-[22px] text-black md:leading-[32px] text-[17px] md:text-[22px] mt-[18px] mb-2">
                      {item.title}
                    </h4>
                    <p className="mb-[30px] text-left text-[13px] font-normal leading-[18px] text-black/75 md:mb-0 md:text-[15px] md:leading-[22px]">
                      {item.description}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 最新资讯 */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">最新资讯</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              获取东南亚地区最新的行业动态和市场洞察
            </p>
          </div>

          {isLoadingArticles ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                <p className="text-gray-600">加载中...</p>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {latestArticles.map((article) => (
                <a
                  key={article.id}
                  href={`/zh/news/detail/${article.id}`}
                  className="group bg-white rounded-lg overflow-hidden shadow-sm hover:shadow-lg transition-all duration-300"
                >
                  <div className="relative h-48 overflow-hidden bg-gray-100">
                    <img
                      src={article.image}
                      alt={article.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.src = `https://readdy.ai/api/search-image?query=$%7BencodeURIComponent%28%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20article.category%20%20%20%20%20%20%20article.title%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%29%7D&width=800&height=400&seq=${article.id}&orientation=landscape`;
                      }}
                    />
                    <div className="absolute top-4 left-4">
                      <span
                        className={`px-3 py-1 text-xs font-medium rounded-full ${
                          article.columnType === 'industry'
                            ? 'bg-purple-600 text-white'
                            : 'bg-orange-600 text-white'
                        }`}
                      >
                        {article.columnType === 'industry' ? '行业洞察' : '国家洞察'}
                      </span>
                    </div>
                  </div>
                  <div className="p-6">
                    <div className="flex items-center space-x-2 mb-3">
                      <span
                        className={`px-2 py-1 text-xs rounded ${
                          article.columnType === 'industry'
                            ? 'bg-blue-100 text-blue-600'
                            : 'bg-green-100 text-green-600'
                        }`}
                      >
                        {article.category}
                      </span>
                      <span className="text-xs text-gray-500">{article.date}</span>
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-blue-600 transition-colors">
                      {article.title}
                    </h3>
                    <p className="text-gray-600 text-sm line-clamp-3">
                      {article.summary}
                    </p>
                  </div>
                </a>
              ))}
            </div>
          )}

          <div className="text-center mt-12">
            <a
              href="/zh/news"
              className="inline-flex items-center px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              查看更多资讯
              <i className="ri-arrow-right-line ml-2"></i>
            </a>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-blue-800 text-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl lg:text-5xl font-bold mb-6">
            开启您的数字化转型之旅
          </h2>
          <p className="text-xl mb-8 opacity-90">
            与亚数智库携手，共同探索数字化未来的无限可能
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="/zh/contact-us"
              className="bg-white text-blue-600 px-8 py-4 rounded-lg hover:bg-gray-100 transition-colors font-semibold whitespace-nowrap"
            >
              联系我们
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="flex h-28 items-center justify-center bg-[#F7F7F7]">
        <span className="text-lg font-semibold text-black/85">战略合作伙伴: </span>
        <a 
          href="https://www.flanexus.com/" 
          target="_blank" 
          rel="noopener noreferrer"
          className="ml-1 text-lg font-semibold underline decoration-[#2D66F4] text-[#2D66F4] hover:text-blue-700"
        >
          炎禾智联
        </a>
        <div className="ml-8">
          <a 
            href="https://readdy.ai/?origin=logo" 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-sm text-gray-500 hover:text-gray-700"
          >
            Powerd by AI
          </a>
        </div>
      </footer>
    </div>
  );
};

export default Home;
